import React from 'react';
import { FaTimes } from 'react-icons/fa';
import { useGetAttachedProductImpact } from '../queries/advertisement';
import Chip from './Chip';

const AttachedProductImpactModal = ({ isOpen, onClose, item, location, contentType = 'content' }) => {
    const hasAttachedProduct = !!item?.attachedProduct;

    // Only make API call when modal is open, we have an item ID, and the item has an attached product
    const { data: apiData = {}, isPending, isError, error } = useGetAttachedProductImpact(
        location,
        isOpen && hasAttachedProduct && item?._id ? item._id : null
    );

    // Sample data for testing - replace with actual API data when available
    const sampleData = {
        totalImpressions: 16,
        uniqueImpressions: 2,
        totalClicks: 4,
        uniqueClicks: 2,
        impressionCtr: "25.00%",
        actualCtr: "100.00%",
        averageCtr: "62.50%",
        productDetails: {
            _id: "68ad9776ddba81186c84b4f4",
            companyId: "68ad95c3ddba81186c84b47e",
            ngoId: null,
            brandId: "68ad9653ddba81186c84b491",
            type: "Service",
            imageUrl: "https://ccappbuckets3.s3.ap-south-1.amazonaws.com/product/cover-image/68ad9776ddba81186c84b4f4.png",
            name: "Apple Care+",
            shortDescription: "Apple Care+",
            longDescription: "Apple care for products",
            remarks: "",
            additionalImages: [],
            isActive: true,
            createdAt: "2025-08-26T11:16:06.555Z",
            updatedAt: "2025-09-17T12:44:54.717Z",
            __v: 0,
            categoryId: "68ad9667ddba81186c84b49a",
            link: "https://www.apple.com/applecare/"
        }
    };

    // Use sample data for now - can be switched to apiData when API is ready
    const impactData = Object.keys(apiData).length > 0 ? apiData : sampleData;

    if (!isOpen) return null;

    if (!hasAttachedProduct) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex flex-col items-center justify-center p-8">
                        <div className="text-lg font-medium text-gray-700 mb-2">No Product Attached</div>
                        <div className="text-sm text-secondary mb-4">This {contentType} does not have a product (sticky ad) associated with it.</div>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isPending) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-center p-8">
                        <div className="text-lg">Loading impact details...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex flex-col items-center justify-center p-8">
                        <div className="text-lg text-red-600 mb-4">Error loading impact details</div>
                        <div className="text-sm text-secondary mb-4">{error?.message || 'An unexpected error occurred'}</div>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-xl font-bold text-gray-900">
                        Advertisement Impact: {item?.title || item?.name || 'N/A'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        title="Close"
                    >
                        <FaTimes className="text-secondary" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Product Details */}
                    {impactData.productDetails && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Details</h3>
                            <div className="flex flex-col md:flex-row gap-4">
                                {/* Product Image */}
                                <div className="flex-shrink-0">
                                    <img 
                                        src={impactData.productDetails.imageUrl} 
                                        alt={impactData.productDetails.name}
                                        className="w-32 h-32 object-contain rounded-lg border border-border p-3"
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/128x128?text=No+Image';
                                        }}
                                    />
                                </div>
                                
                                {/* Product Info */}
                                <div className="flex-1 space-y-3">
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-900">
                                            {impactData.productDetails.name}
                                        </h4>
                                        <Chip
                                            value={impactData.productDetails.type || 'Unknown'}
                                            color={impactData.productDetails.type === 'Product' ? 'blue' : 'purple'}
                                            small={true}
                                        />
                                    </div>
                                    
                                    <div>
                                        <p className="text-secondary text-base leading-relaxed">
                                            {impactData.productDetails.longDescription}
                                        </p>
                                    </div>
                                    
                                    {impactData.productDetails.link && (
                                        <div>
                                            <a 
                                                href={impactData.productDetails.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-primary hover:text-primary/80 transition-colors text-base font-medium"
                                            >
                                                <span>Visit Product Page</span>
                                                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Impact Metrics */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Impact Summary</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white rounded-lg p-4 text-center shadow">
                                <div className="text-2xl font-bold text-primary">
                                    {impactData.totalImpressions || 0}
                                </div>
                                <div className="text-sm text-secondary mt-1">Total Impressions</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 text-center shadow">
                                <div className="text-2xl font-bold text-chipBlue">
                                    {impactData.uniqueImpressions || 0}
                                </div>
                                <div className="text-sm text-secondary mt-1">Unique Impressions</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 text-center shadow">
                                <div className="text-2xl font-bold text-chipPurple">
                                    {impactData.totalClicks || 0}
                                </div>
                                <div className="text-sm text-secondary mt-1">Total Clicks</div>
                            </div>
                            <div className="bg-white rounded-lg p-4 text-center shadow">
                                <div className="text-2xl font-bold text-chipYellow">
                                    {impactData.uniqueClicks || 0}
                                </div>
                                <div className="text-sm text-secondary mt-1">Unique Clicks</div>
                            </div>
                        </div>

                        {/* Additional Metrics - CTR Values */}
                        {(impactData.impressionCtr !== undefined || impactData.actualCtr !== undefined || impactData.averageCtr !== undefined) && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                {impactData.impressionCtr !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-primary">
                                        <div className="text-xl font-bold text-primary">
                                            {impactData.impressionCtr}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Impression CTR</div>
                                    </div>
                                )}
                                {impactData.actualCtr !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-chipBlue">
                                        <div className="text-xl font-bold text-chipBlue">
                                            {impactData.actualCtr}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Actual CTR</div>
                                    </div>
                                )}
                                {impactData.averageCtr !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-chipPurple">
                                        <div className="text-xl font-bold text-chipPurple">
                                            {impactData.averageCtr}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Average CTR</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Legacy CTR Support - for backward compatibility */}
                        {(impactData.clickThroughRate !== undefined || impactData.uniqueClickThroughRate !== undefined || impactData.ctr !== undefined) && 
                         !impactData.impressionCtr && !impactData.actualCtr && !impactData.averageCtr && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                {impactData.ctr !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-primary">
                                        <div className="text-xl font-bold text-primary">
                                            {impactData.ctr}
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Click Through Rate</div>
                                    </div>
                                )}
                                {impactData.clickThroughRate !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-primary">
                                        <div className="text-xl font-bold text-primary">
                                            {impactData.clickThroughRate.toFixed(2)}%
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Click Through Rate</div>
                                    </div>
                                )}
                                {impactData.uniqueClickThroughRate !== undefined && (
                                    <div className="bg-white rounded-lg p-4 text-center shadow border-l-4 border-chipBlue">
                                        <div className="text-xl font-bold text-chipBlue">
                                            {impactData.uniqueClickThroughRate.toFixed(2)}%
                                        </div>
                                        <div className="text-sm text-secondary mt-1">Unique CTR</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* No Data Message */}
                    {!impactData.totalImpressions && !impactData.totalClicks && (
                        <div className="text-center py-8 text-gray-500">
                            <div className="text-lg font-medium">No impact data available</div>
                            <div className="text-sm mt-2">This {contentType} hasn't generated any impressions or clicks yet.</div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AttachedProductImpactModal;